import {
  markNotificationsAsRead,
  markNotificationsAsUnRead,
  Notification,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { MouseEvent, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

type UseNotificationListElementParams = {
  notification: Notification;
} & (
  | {
      type: "mobile";
      closeNotificationDrawer?: never;
      setNotificationRead?: never;
      updateHasNewNotifications?: never;
    }
  | {
      type: "desktop";
      closeNotificationDrawer?: () => void;
      setNotificationRead: (notificationId: string, isRead: boolean) => void;
      updateHasNewNotifications: () => void;
    }
);

export function useNotificationListElement({
  notification,
  closeNotificationDrawer,
  setNotificationRead,
  updateHasNewNotifications,
  type,
}: UseNotificationListElementParams) {
  const { lang } = useContext(PspContext);
  const isTablet = useIsTablet();
  const navigate = useNavigate();

  async function onClick(notificationId: string) {
    markNotificationsAsRead({
      query: { notification_id: notificationId },
    }).then(({ error }) => {
      if (error) {
        toast(translate("notificationCouldNotBeMarkedAsRead", lang));
      }
    });
    closeNotificationDrawer?.();

    switch (notification.action.type) {
      case "document_for_client":
        navigate("/psp-documents");
        break;
      case "message_for_client":
        navigate("/messages");
        break;
      case "note_from_client":
        if (notification.mandate) {
          navigate(`/admin/submitted-documents/${notification.mandate.id}`);
        }
        break;
    }
  }

  async function toggleRead(event: MouseEvent<HTMLButtonElement>) {
    if (type === "desktop") {
      event.stopPropagation();
      if (notification.readAt) {
        const { error } = await markNotificationsAsUnRead({
          query: { notification_id: notification.id },
        });
        if (error) {
          toast(translate("somethingWentWrong", lang));
          throw new Error("Could not mark notification as unread.");
        }
        setNotificationRead(notification.id, false);
      } else {
        const { error } = await markNotificationsAsRead({
          query: { notification_id: notification.id },
        });
        if (error) {
          toast(translate("somethingWentWrong", lang));
          throw new Error("Could not mark notification as read.");
        }
        setNotificationRead(notification.id, true);
      }
      updateHasNewNotifications();
    }
  }

  return { lang, isTablet, onClick, toggleRead };
}
